import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../contexts/AuthContext';
import { getAllUsers, setUserBlocked, setUserAdmin, updateUser, type UserInfo } from '../services/users';

function EditUserDialog({ user: target, onClose, onSave }: {
  user: UserInfo;
  onClose: () => void;
  onSave: (updated: Partial<UserInfo>) => void;
}) {
  const [displayName, setDisplayName] = useState(target.displayName ?? '');
  const [inboundHandle, setInboundHandle] = useState(target.inboundHandle ?? '');
  const [inboundHandle2, setInboundHandle2] = useState(target.inboundHandle2 ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await updateUser(target.uid, { displayName, inboundHandle, inboundHandle2 });
    onSave({ displayName, inboundHandle, inboundHandle2 });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit user — {target.email}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Inbound email handle"
            value={inboundHandle}
            onChange={(e) => setInboundHandle(e.target.value)}
            fullWidth
            size="small"
            helperText={`${inboundHandle || '<handle>'}@stash.memespin.com`}
          />
          <TextField
            label="Inbound email handle 2"
            value={inboundHandle2}
            onChange={(e) => setInboundHandle2(e.target.value)}
            fullWidth
            size="small"
            helperText={inboundHandle2 ? `${inboundHandle2}@stash.memespin.com` : 'Optional second handle'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserInfo | null>(null);

  useEffect(() => {
    getAllUsers().then((all) => {
      setUsers(all);
      setLoading(false);
    });
  }, []);

  const handleToggleBlock = async (target: UserInfo) => {
    setToggling(target.uid);
    const newBlocked = !target.isBlocked;
    await setUserBlocked(target.uid, newBlocked);
    setUsers((prev) => prev.map((u) => u.uid === target.uid ? { ...u, isBlocked: newBlocked } : u));
    setToggling(null);
  };

  const handleToggleAdmin = async (target: UserInfo) => {
    setToggling(target.uid);
    const newAdmin = !target.isAdmin;
    await setUserAdmin(target.uid, newAdmin);
    setUsers((prev) => prev.map((u) => u.uid === target.uid ? { ...u, isAdmin: newAdmin } : u));
    setToggling(null);
  };

  const handleEditSave = (updated: Partial<UserInfo>) => {
    if (!editing) return;
    setUsers((prev) => prev.map((u) => u.uid === editing.uid ? { ...u, ...updated } : u));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>
      <Stack spacing={1}>
        {users.map((u) => {
          const isSelf = u.uid === user?.uid;
          const busy = toggling === u.uid;
          return (
            <Paper key={u.uid} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar src={u.photoURL} alt={u.displayName} sx={{ width: 36, height: 36 }}>
                {u.displayName?.[0]}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>{u.displayName}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography>
                {(u.inboundHandle || u.inboundHandle2) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                    {[u.inboundHandle, u.inboundHandle2].filter(Boolean).map(h => `${h}@stash.memespin.com`).join(', ')}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {u.isAdmin && <Chip label="admin" size="small" color="primary" variant="outlined" />}
                {u.isBlocked && <Chip label="blocked" size="small" color="error" variant="outlined" />}
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(u)}>
                  Edit
                </Button>
                <Tooltip title={isSelf ? 'Cannot change your own admin status' : ''}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color={u.isAdmin ? 'warning' : 'primary'}
                      disabled={busy || isSelf}
                      onClick={() => handleToggleAdmin(u)}
                    >
                      {u.isAdmin ? 'Remove admin' : 'Make admin'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={isSelf ? 'Cannot block yourself' : ''}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color={u.isBlocked ? 'primary' : 'error'}
                      disabled={busy || isSelf}
                      onClick={() => handleToggleBlock(u)}
                    >
                      {u.isBlocked ? 'Unblock' : 'Block'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSave={handleEditSave}
        />
      )}
    </Box>
  );
}
